import { t } from "@lingui/macro";
import { RouteProp, useRoute } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useSetAtom } from "jotai";
import { useEffect, useState } from "react";
import { useFragment } from "react-relay";

import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import {
  CreditCardIcon,
  BellIcon,
  LogOutIcon,
  WhooshCrownIcon,
  SettingsIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { NotificationsUpdateModal } from "../../../../features/NotificationsUpdateModal";
import { ProfileStackNavigatorParamList } from "../../../../navigators/ProfileStackNavigator/types";
import { useFlags } from "../../../../utils/launchDarkly";
import DeleteAccountModal from "../DeleteAccountModal";
import { SettingsButton } from "../SettingsButton";
import { AccountSettings_Club$key } from "./__generated__/AccountSettings_Club.graphql";
import { AccountModal } from "./components/AccountModal";
import { ManageClubsModal } from "./components/ManageClubsModal";
import { ManageNotificationsModal } from "./components/ManageNotificationsModal";
import { isVerifyingEmailOrPhoneAtom } from "./components/ManageNotificationsModal/atoms/stores";
import { PaymentMethodsModal } from "./components/PaymentMethodsModal";
import { SquareCardEntryModalWrapper } from "./components/SquareCardEntryModalWrapper";

type AccountSettingsProps = {
  setIsSignOutModalOpen: (value: boolean) => void;
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  fragmentRef: AccountSettings_Club$key;
};

export const AccountSettings = ({
  setIsSignOutModalOpen,
  isOpen,
  setIsOpen,
  fragmentRef,
}: AccountSettingsProps) => {
  const club = useFragment(AccountSettingsFragmentString, fragmentRef);
  const [isManageClubsOpen, setIsManageClubsOpen] = useState(false);
  const [cardEntryError, setCardEntryError] = useState<string>();
  const [isPaymentMethodsModalOpen, setIsPaymentMethodsModalOpen] =
    useState(false);

  const [isAddACardModalOpen, setIsAddACardModalOpen] = useState(false);

  const route =
    useRoute<RouteProp<ProfileStackNavigatorParamList, "ProfileHome">>();
  const defaultNotificationsOpen = !!route.params?.openNotificationSettings;

  const { waitlistRestrictions } = useFlags();

  const trackEvent = useTrackEvent();
  const [isNotificationsUpdateOpen, setIsNotificationsUpdateOpen] = useState(
    defaultNotificationsOpen && !!waitlistRestrictions
  );

  const [isManageNotificationsOpen, setIsManageNotificationsOpen] = useState(
    defaultNotificationsOpen && !waitlistRestrictions
  );

  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] =
    useState(false);

  const shouldShowPaymentMethods = !!club.supportsPublic;

  const setLegacyVerificationMode = useSetAtom(isVerifyingEmailOrPhoneAtom);

  useEffect(() => {
    if (!defaultNotificationsOpen) return;
    if (waitlistRestrictions) {
      setIsNotificationsUpdateOpen(true);
      setIsManageNotificationsOpen(false);
    } else {
      setIsManageNotificationsOpen(true);
      setIsNotificationsUpdateOpen(false);
    }
  }, [defaultNotificationsOpen, waitlistRestrictions]);

  const openNotificationsModal = () => {
    if (waitlistRestrictions) {
      trackEvent("CustomerProfile.WaitlistNotificationModalOpened", {});
      setIsNotificationsUpdateOpen(true);
    } else {
      setIsManageNotificationsOpen(true);
    }
  };

  const handleRequestVerification = (mode: "email" | "phone") => {
    setLegacyVerificationMode(mode);
    setIsManageNotificationsOpen(true);
    setIsNotificationsUpdateOpen(false);
  };

  return (
    <>
      <VStack flex={1}>
        <Box py={2}>
          <Text variant="mainSmallSemiBold">{t`Whoosh Account Settings`}</Text>
        </Box>

        <VStack>
          <SettingsButton
            icon={<WhooshCrownIcon />}
            title={t`Account`}
            onPress={() => setIsOpen(true)}
            showDivider
          />
          {shouldShowPaymentMethods ? (
            <SettingsButton
              icon={<CreditCardIcon />}
              title={t`Payment Methods`}
              onPress={() => {
                setCardEntryError(undefined);
                setIsPaymentMethodsModalOpen(true);
              }}
              showDivider
              testID="payment-methods-button"
            />
          ) : null}
          <SettingsButton
            icon={<BellIcon color="black" />}
            title={t`Notifications`}
            onPress={openNotificationsModal}
            showDivider
          />
          <SettingsButton
            icon={<SettingsIcon />}
            title={t`Manage Clubs`}
            onPress={() => setIsManageClubsOpen(true)}
            showDivider
          />
          <SettingsButton
            icon={<LogOutIcon color="status-error-500" />}
            title={t`Log Out`}
            onPress={() => setIsSignOutModalOpen(true)}
            showDivider
            variant="danger"
            accessibilityLabel={t`Log Out From Whoosh Staff App`}
          />
        </VStack>
      </VStack>
      {/* this variable stops rendering because the query needs to refire when the modal is opened. and if its not conditionally rendered it wont load. */}
      {isNotificationsUpdateOpen ? (
        <NotificationsUpdateModal
          isOpen={isNotificationsUpdateOpen}
          setIsOpen={setIsNotificationsUpdateOpen}
          onRequestVerification={handleRequestVerification}
        />
      ) : null}
      {isManageNotificationsOpen ? (
        <ManageNotificationsModal
          isOpen={isManageNotificationsOpen}
          setIsOpen={setIsManageNotificationsOpen}
          onVerificationComplete={
            waitlistRestrictions
              ? () => {
                  setIsManageNotificationsOpen(false);
                  setIsNotificationsUpdateOpen(true);
                }
              : undefined
          }
        />
      ) : null}
      <ManageClubsModal
        isOpen={isManageClubsOpen}
        setIsOpen={setIsManageClubsOpen}
      />
      <AccountModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        setIsDeleteAccountModalOpen={setIsDeleteAccountModalOpen}
      />
      <DeleteAccountModal
        isOpen={isDeleteAccountModalOpen}
        setIsOpen={setIsDeleteAccountModalOpen}
      />
      <PaymentMethodsModal
        errorMessage={cardEntryError}
        isOpen={isPaymentMethodsModalOpen}
        setIsOpen={setIsPaymentMethodsModalOpen}
        setIsAddACardModalOpen={(isOpen) => {
          setCardEntryError(undefined);
          setIsAddACardModalOpen(isOpen);
        }}
      />
      <SquareCardEntryModalWrapper
        isOpen={isAddACardModalOpen}
        setIsOpen={setIsAddACardModalOpen}
        onReturnToPaymentMethods={(message) => {
          setCardEntryError(message);
          setIsPaymentMethodsModalOpen(true);
        }}
      />
    </>
  );
};

const AccountSettingsFragmentString = graphql`
  fragment AccountSettings_Club on Club {
    supportsPublic
  }
`;
